import React, { useEffect, useState } from 'react';
import Empty from '../empty';
import { Col, Row } from 'antd';
import { Img } from 'react-image';
import { Menu } from '@alifd/next';
import classNames from 'classnames';
import Requests from '@/services/request';
import TableFilter from '@/components/TableFilter';
import { getQueryStringArgs } from '@/utils/url-parse';
import { Table, Dropdown, Loading } from '@b-design/fusion';
import ViewVersionDrawer from './component/viewVersionDrawer';
import EditAliasNameModal from './component/editAliasNameModal';
import PaginationComponent from '@/components/PaginationComponent';
import ViewFlowDetailsDrawer from './component/viewFlowDetailsDrawer';
import { GetColumns, FilterKeys, CardTypeList, RenderTooltip, WorkflowItemType } from './const';
import $i18n from '../../../../i18n';
import './index.less';

interface PropsType {
  contentHeight: number;
}

const WorkflowManagement: React.FC<PropsType> = ({ contentHeight }) => {
  const { spaceId } = getQueryStringArgs();
  const [loading, setLoading] = useState(false);
  const [listType, setListType] = useState('table'); // table | card
  const [filterValue, setFilterValue] = useState<any>({
    current: 1,
    size: 10,
  });
  const [tableData, setTableData] = useState<{ totalCount: number; data: WorkflowItemType[] }>({
    totalCount: 0,
    data: [],
  }); // 列表数据和总条数
  const [editVisible, setEditVisible] = useState(false);
  const [editRecord, setEditRecord] = useState();
  const [versionVisible, setVersionVisible] = useState(false);
  const [flowDetailsVisible, setFlowDetailsVisible] = useState(false);

  /**
   * 条件查询处理
   * @param filterParams 询筛选参数
   */
  const searchHandler = (filterParams) => {
    const payload = {
      ...filterValue,
      ...filterParams,
      current: 1,
    };

    setFilterValue(payload);
  };

  /**
   * 初始化表单数据请求
   */
  const initData = () => {
    setLoading(true);
    Requests.get('/rest/intercnn/aigc/workflow/list', { ...filterValue, spaceId, search: encodeURIComponent(filterValue?.search || '') })
      .then((data) => {
        const { total = 0, list = [] } = data || {};
        setTableData({
          totalCount: total,
          data: list,
        });
        setLoading(false);
      })
      .catch(() => {
        setTableData({
          totalCount: 0,
          data: [],
        });
        setLoading(false);
      });
  };

  useEffect(() => {
    initData();
  }, [filterValue]);

  /**
   * 查看工作流详情
   */
  const viewFlowDetails = (item: any) => {
    setEditRecord(item);
    setFlowDetailsVisible(true);
  };

  /**
   * 表格类型
   */
  const renderTable = () => {
    return (
      <Table.StickyLock
        fixedHeader
        loading={loading}
        hasBorder={false}
        primaryKey="id"
        dataSource={tableData.data || []}
        columns={GetColumns(viewFlowDetails, onItemClick)}
        maxBodyHeight={contentHeight - 48}
        emptyContent={
          <span>
            {$i18n.get({
              id: 'aopf.monitoringDashboard.graphicComponent.NoDataAvailable',
              dm: '暂无数据',
            })}
          </span>
        }
      />
    );
  };

  /**
   * 编辑工作流别名 ｜ 版本管理
   */
  const onItemClick = (key: string, item: any) => {
    setEditRecord(item);
    // 编辑工作流别名
    if (key === 'editAliasName') {
      setEditVisible(true);
    }

    // 版本管理
    if (key === 'viewVersion') {
      setVersionVisible(true);
    }
  };

  /**
   * 卡片类型
   */
  const renderCard = () => {
    const _data = tableData.data || [];
    return (
      <div className="card-container">
        {!_data.length ? (
          <Empty />
        ) : (
          <Row gutter={[24, 24]} wrap>
            {(tableData.data || []).map((item) => {
              const { alias, name, identify, type, version, isCustom } = item;
              return (
                <Col
                  span={6}
                  className="card-item"
                  key={identify}
                  onClick={() => viewFlowDetails(item)}
                >
                  <div className="card-top">
                    <div className="card-header">
                      <div className="type">
                        {$i18n.get(
                          {
                            id: 'aopf.component.workflowManagement.TypeType',
                            dm: '类型：{type}',
                          },
                          { type: type },
                        )}
                      </div>
                      {`${isCustom}` === '1' &&
                        RenderTooltip(
                          $i18n.get({
                            id: 'aopf.component.workflowManagement.CustomWorkflow',
                            dm: '定制工作流',
                          }),
                          <img
                            className="customized-icon"
                            src="https://gw.alicdn.com/imgextra/i2/O1CN01dtDy5l1jWF60kLibD_!!6000000004555-2-tps-60-63.png"
                          />,
                        )}
                    </div>
                    {RenderTooltip(name, <div className="flow-name">{name || ''}</div>)}
                    {RenderTooltip(
                      alias,
                      <div className="flow-alias-name">{alias ? `-${alias}` : ''}</div>,
                    )}
                    <div className="flow-update-info">
                      <div className="update-info">
                        {RenderTooltip(identify, <div className="label">ID: {identify || ''}</div>)}
                        <div>{version ? `V ${version}` : ''}</div>
                      </div>
                      <Dropdown trigger={<div className="action" onClick={(e)=> e.stopPropagation()}>···</div>} triggerType={['hover']}>
                        <Menu onItemClick={(key) => onItemClick(key, item)}>
                          <Menu.Item
                            key="editAliasName"
                            className="workflow-management-dropdown-menu-item"
                          >
                            <img src="https://gw.alicdn.com/imgextra/i3/O1CN01T12eoo1btaM8rvATd_!!6000000003523-2-tps-42-42.png" />
                            {$i18n.get({
                              id: 'aopf.component.workflowManagement.WorkflowAlias',
                              dm: '工作流别名',
                            })}
                          </Menu.Item>
                          <Menu.Item
                            key="viewVersion"
                            className="workflow-management-dropdown-menu-item"
                          >
                            <img src="https://gw.alicdn.com/imgextra/i2/O1CN01uKaLWA1Lfob29RWcK_!!6000000001327-2-tps-42-42.png" />
                            {$i18n.get({
                              id: 'aopf.component.workflowManagement.VersionManagement',
                              dm: '版本管理',
                            })}
                          </Menu.Item>
                        </Menu>
                      </Dropdown>
                    </div>
                  </div>
                  <div className="flow-img-container">
                    <Img
                      src={item.image || ''}
                      loader={
                        <Loading>
                          <img src="https://gw.alicdn.com/imgextra/i4/O1CN019ffzbJ1Em2g1oeLTr_!!6000000000393-2-tps-774-540.png" />
                        </Loading>
                      }
                      unloader={
                        <img src="https://gw.alicdn.com/imgextra/i4/O1CN019ffzbJ1Em2g1oeLTr_!!6000000000393-2-tps-774-540.png" />
                      }
                    />
                  </div>
                </Col>
              );
            })}
          </Row>
        )}
      </div>
    );
  };

  return (
    <div className="workflow-management">
      <div className="filter-header">
        <TableFilter filterKeys={FilterKeys} filterHandle={searchHandler} hideFilterSelect={true} />
        <div className="card-type-switch">
          {CardTypeList.map((v) => (
            <div
              key={v.key}
              className={classNames({
                'switch-item': true,
                'switch-item-active': listType === v.key,
              })}
              onClick={() => {
                if (v.key !== listType) {
                  setListType(v.key);
                }
              }}
            >
              <img src={listType === v.key ? v.activeImg : v.img} />
            </div>
          ))}
        </div>
      </div>
      {listType === 'table' ? renderTable() : renderCard()}
      {tableData?.totalCount !== 0 && (
        <PaginationComponent
          total={tableData?.totalCount}
          current={filterValue?.current}
          pageSize={filterValue?.size}
          onPageChange={(_current, _size) => {
            setFilterValue({
              ...filterValue,
              current: _current,
              size: _size,
            });
          }}
        />
      )}

      <EditAliasNameModal
        visible={editVisible}
        setVisible={setEditVisible}
        record={editRecord || {}}
        updateData={initData}
      />

      <ViewVersionDrawer
        visible={versionVisible}
        setVisible={setVersionVisible}
        record={editRecord || {}}
      />

      <ViewFlowDetailsDrawer
        visible={flowDetailsVisible}
        setVisible={setFlowDetailsVisible}
        record={editRecord || {}}
      />
    </div>
  );
};
export default WorkflowManagement;
